import { getHours, getMinutes, differenceInMinutes, isValid } from 'date-fns';
import { Shift, DayShift, HourRange, RelativeBreak } from '@/types';
// import { enhancedShiftPositioningService, PositionResult } from './enhanced-shift-positioning-service';

// Temporary placeholder types and service until enhanced-shift-positioning-service is implemented
interface PositionResult {
  left: number;
  width: number;
  top?: number;
}

const enhancedShiftPositioningService = {
  // Add placeholder methods as needed
};

// Helper function to detect overlaps between shifts
export const doShiftsOverlap = (shift1: Shift | DayShift, shift2: Shift | DayShift): boolean => {
  return (
    shift1.startTime < shift2.endTime &&
    shift2.startTime < shift1.endTime
  );
};

// Helper function to assign shifts to rows for display
export interface ShiftRow {
  rowIndex: number;
  shifts: (Shift | DayShift)[];
}

// Optimized row organization with O(n log n) complexity
export const organizeShiftsIntoRows = (shifts: (Shift | DayShift)[]): ShiftRow[] => {
  if (shifts.length === 0) return [];

  const rows: ShiftRow[] = [];
  // Track the end time of the last shift in each row for faster overlap detection
  const rowEndTimes: Date[] = [];

  // Sort shifts by start time once - O(n log n)
  const sortedShifts = [...shifts].sort((a, b) => a.startTime.getTime() - b.startTime.getTime());

  for (const shift of sortedShifts) {
    let assignedToRow = false;

    // Find the first row where this shift can fit - O(k) where k is number of rows
    // In practice, k is much smaller than n for most scheduling scenarios
    for (let i = 0; i < rows.length; i++) {
      // Quick check: if shift starts after the row's last end time, it fits
      if (shift.startTime >= rowEndTimes[i]) {
        rows[i].shifts.push(shift);
        rowEndTimes[i] = shift.endTime;
        assignedToRow = true;
        break;
      }
    }

    // If no suitable row found, create a new one
    if (!assignedToRow) {
      rows.push({
        rowIndex: rows.length,
        shifts: [shift]
      });
      rowEndTimes.push(shift.endTime);
    }
  }

  return rows;
};

// Helper function to format duration in hours and minutes
export const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours > 0 ? `${hours}h ` : ''}${mins > 0 ? `${mins}m` : ''}`;
};

// **PERFORMANCE OPTIMIZATION**: Position calculation now handled by EnhancedShiftPositioningService
// Legacy cache functions maintained for backward compatibility

// Get cache statistics (delegates to enhanced service)
export const getPositionCacheStats = () => {
  const stats = enhancedShiftPositioningService.getCacheStatistics();
  return {
    size: stats.positionCacheSize,
    hitCount: stats.positionHitCount,
    missCount: stats.positionMissCount,
    hitRate: stats.positionHitRate
  };
};

// Helper function to get position and width for shifts and hour ranges in pixels
// **MIGRATED**: This function has been moved to positioning-compatibility-layer.ts
// Components should import directly from there or use the enhanced service
// Removed export to prevent conflicting star exports


// Validate breaks to ensure they don't extend beyond shift duration or overlap (from DayShiftPanel.tsx)
export const validateBreaks = (breaks: RelativeBreak[], shiftDuration: number): boolean => {
  // Sort breaks by start time for overlap checking
  const sortedBreaks = [...breaks].sort((a, b) => a.startMinute - b.startMinute);
  
  for (let i = 0; i < sortedBreaks.length; i++) {
    const breakItem = sortedBreaks[i];
    
    // Check if break starts within shift duration
    if (breakItem.startMinute < 0 || breakItem.startMinute >= shiftDuration) {
      // toast.error(`Break #${i + 1} starts outside the shift duration`); // Removed toast here, will handle in component
      return false;
    }
    
    // Check if break ends within shift duration
    if (breakItem.startMinute + breakItem.durationMinutes > shiftDuration) {
      // toast.error(`Break #${i + 1} extends beyond the shift duration`); // Removed toast here, will handle in component
      return false;
    }
    
    // Check for overlap with next break
    if (i < sortedBreaks.length - 1) {
      const nextBreak = sortedBreaks[i + 1];
      if (breakItem.startMinute + breakItem.durationMinutes > nextBreak.startMinute) {
        // toast.error(`Break #${i + 1} overlaps with the next break`); // Removed toast here, will handle in component
        return false;
      }
    }
  }
  
  return true;
};

// Check if an employee is available for a specific shift time slot
export interface EmployeeAvailability {
  employeeId: string;
  isAvailable: boolean;
  conflictingShifts?: {
    id: string;
    startTime: Date;
    endTime: Date;
    title?: string;
  }[];
}

export const isEmployeeAvailableForShift = (
  employeeId: string,
  proposedStartTime: Date,
  proposedEndTime: Date,
  existingShifts: (Shift | DayShift)[],
  excludeShiftId?: string
): EmployeeAvailability => {
  // Filter shifts for this employee, excluding the current shift being edited
  const employeeShifts = existingShifts.filter(
    shift => shift.employeeId === employeeId && shift.id !== excludeShiftId
  );

  // Find conflicting shifts
  const conflictingShifts = employeeShifts.filter(shift => 
    doShiftsOverlap(
      { startTime: proposedStartTime, endTime: proposedEndTime } as Shift,
      shift
    )
  );

  return {
    employeeId,
    isAvailable: conflictingShifts.length === 0,
    conflictingShifts: conflictingShifts.map(shift => ({
      id: shift.id,
      startTime: shift.startTime,
      endTime: shift.endTime,
      title: shift.title
    }))
  };
};

// Check availability for multiple employees at once
export const checkMultipleEmployeeAvailability = (
  employees: { id: string; name: string }[],
  proposedStartTime: Date,
  proposedEndTime: Date,
  existingShifts: (Shift | DayShift)[],
  excludeShiftId?: string
): Map<string, EmployeeAvailability> => {
  const availabilityMap = new Map<string, EmployeeAvailability>();

  employees.forEach(employee => {
    const availability = isEmployeeAvailableForShift(
      employee.id,
      proposedStartTime,
      proposedEndTime,
      existingShifts,
      excludeShiftId
    );
    availabilityMap.set(employee.id, availability);
  });

  return availabilityMap;
};
